<template>
    <div class="relative mb-6 w-full">
        <div class="relative w-full">
            <textarea
                ref="textareaRef"
                class="peer block min-h-[100px] w-full resize-y overflow-y-auto rounded-input border px-3 pt-3 text-sm focus:border-focus focus:outline-none focus:ring-0"
                :class="{
                    'border-error': errorMessage,
                    'border-muval-gray-4 bg-white hover:border-muval-gray-3': !errorMessage,
                }"
                :rows="rows"
                :name="vid"
                :placeholder="placeholder"
                :value="inputValue"
                :maxlength="limit"
                @input="onInput"
                @keydown="onKeydown"
                @blur="onBlur"
            />

            <!-- @mention suggestions -->
            <ul
                v-if="showSuggestions && filteredStaff.length"
                class="absolute z-50 mt-1 max-h-56 w-72 overflow-y-auto rounded-md border border-muval-gray-4 bg-white py-1 shadow-lg"
            >
                <li
                    v-for="(staff, index) in filteredStaff"
                    :key="staff.id"
                    class="flex cursor-pointer items-center gap-x-2 px-3 py-2 text-sm"
                    :class="{ 'bg-muval-gray-6': index === highlightedIndex }"
                    @mousedown.prevent="selectStaff(staff)"
                    @mouseenter="highlightedIndex = index"
                >
                    <img
                        v-if="staff.avatar"
                        :src="staff.avatar"
                        class="size-6 shrink-0 rounded-full object-cover"
                    />
                    <span
                        v-else
                        class="flex size-6 shrink-0 items-center justify-center rounded-full bg-muval-gray-4 text-xs text-white"
                        >{{ initials(staff.fullname) }}</span
                    >
                    <span class="truncate">{{ staff.fullname }}</span>
                </li>
            </ul>

            <div
                v-if="errorMessage"
                class="absolute -bottom-5 left-0 flex w-full truncate whitespace-nowrap text-xxs text-error"
            >
                <div class="text-xxs">{{ errorMessage }}</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useField } from 'vee-validate';
import { idGen } from '@utilities/idGen';

const props = defineProps({
    vid: {
        type: String,
        required: false,
        default: () => idGen(6),
    },
    placeholder: {
        type: String,
        required: false,
        default: 'Create note',
    },
    rows: {
        type: Number,
        required: false,
        default: 1,
    },
    limit: {
        type: Number,
        required: false,
        default: 3000,
    },
    // Two-way bound array of mentioned user IDs
    mentionedUserIds: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const emit = defineEmits(['update:mentionedUserIds']);

const userStore = useUserStore();

const vid = toRef(props, 'vid');
const { value: inputValue, errorMessage, handleBlur } = useField(vid, undefined, { syncVModel: true });

const textareaRef = ref(null);
const staffList = ref([]);
const showSuggestions = ref(false);
const query = ref('');
const highlightedIndex = ref(0);

// Mentions confirmed via the dropdown: { id, fullname }
const mentions = ref([]);

const MENTION_TRIGGER = /(?:^|\s)@([^\s@]*)$/;

const filteredStaff = computed(() => {
    const q = query.value.toLowerCase();
    return staffList.value
        .filter((staff) => staff.id !== userStore.getUserId)
        .filter((staff) => staff.fullname?.toLowerCase().includes(q))
        .slice(0, 8);
});

function initials(name) {
    return (name || '')
        .split(' ')
        .map((part) => part.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

async function fetchStaff() {
    try {
        const response = await window.tenantAxios.get('users/tenant?limit=500');
        staffList.value = (response.data.data || []).filter((staff) => staff.is_active && !staff.archived);
    } catch (error) {
        staffList.value = [];
        console.error('Failed to load staff for note mentions', error);
    }
}

function onInput(event) {
    inputValue.value = event.target.value;
    detectTrigger();
    syncMentions();
}

function detectTrigger() {
    const el = textareaRef.value;
    if (!el) return;

    const textBeforeCaret = (inputValue.value || '').slice(0, el.selectionStart);
    const match = textBeforeCaret.match(MENTION_TRIGGER);

    if (match) {
        query.value = match[1];
        highlightedIndex.value = 0;
        showSuggestions.value = true;
    } else {
        showSuggestions.value = false;
    }
}

function selectStaff(staff) {
    const el = textareaRef.value;
    const value = inputValue.value || '';
    const caret = el ? el.selectionStart : value.length;

    const textBeforeCaret = value.slice(0, caret);
    const textAfterCaret = value.slice(caret);
    const match = textBeforeCaret.match(MENTION_TRIGGER);
    if (!match) return;

    // Replace the partial "@query" with "@Fullname " preserving any leading whitespace.
    const triggerStart = match.index + match[0].indexOf('@');
    const newBefore = textBeforeCaret.slice(0, triggerStart) + `@${staff.fullname} `;
    inputValue.value = newBefore + textAfterCaret;

    if (!mentions.value.some((mention) => mention.id === staff.id)) {
        mentions.value.push({ id: staff.id, fullname: staff.fullname });
    }

    showSuggestions.value = false;
    syncMentions();

    nextTick(() => {
        if (el) {
            const pos = newBefore.length;
            el.focus();
            el.setSelectionRange(pos, pos);
        }
    });
}

// Keep only mentions whose "@Fullname" text is still present, then emit the IDs.
function syncMentions() {
    const text = inputValue.value || '';
    mentions.value = mentions.value.filter((mention) => text.includes(`@${mention.fullname}`));
    emit(
        'update:mentionedUserIds',
        mentions.value.map((mention) => mention.id),
    );
}

function onKeydown(event) {
    if (!showSuggestions.value || !filteredStaff.value.length) return;

    if (event.key === 'ArrowDown') {
        event.preventDefault();
        highlightedIndex.value = (highlightedIndex.value + 1) % filteredStaff.value.length;
    } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        highlightedIndex.value = (highlightedIndex.value - 1 + filteredStaff.value.length) % filteredStaff.value.length;
    } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        selectStaff(filteredStaff.value[highlightedIndex.value]);
    } else if (event.key === 'Escape') {
        showSuggestions.value = false;
    }
}

function onBlur(event) {
    handleBlur(event);
    // Delay so a mousedown selection on the list still registers.
    setTimeout(() => {
        showSuggestions.value = false;
    }, 150);
}

// Prune mentions when the field is reset/changed externally (e.g. after submit).
watch(inputValue, () => {
    if (!mentions.value.length) return;
    syncMentions();
});

onMounted(fetchStaff);
</script>
